import React, { useEffect, useState } from "react";
import { Accordion, Button, Card } from "react-bootstrap";
import "./OpalOrderRequest.css";
import TauriLoader2 from "../../../TauriLoader2/TauriLoader2";
import TauriImage from "../../../TauriImage/TauriImage";
import OpalAddItemRequestModal from "./OpalAddItemRequestModal/OpalAddItemRequestModal";
import OpalUnitSelectorModal from "./OpalUnitSelectorModal/OpalUnitSelectorModal";
import OpalAddRemarksModal from "./OpalAddRemarksModal/OpalAddRemarksModal";
import { useBaseProvider } from "../../../../BaseManager/BaseManager";
import { addRecord, deleteRecord, deleteRecordByField, getAll, getNextId, getRecordById, updateRecord } from "../../../../localDB";
import { statusMapping, SyncStatusMap } from "../../../../visualizations/visualizations";

const OpalOrderRequest = ({ section, id }) => {
    const [data, setData] = useState([]);
    const [loading, setIsLoading] = useState(false);
    const [checkedRows, setCheckedRows] = useState({});
    const [showItemModal, setShowItemModal] = useState(false);
    const [showUnitModal, setShowUnitModal] = useState(false);
    const [showRemarksModal, setShowRemarksModal] = useState(false);
    const [session, setSession] = useState(null);
    const [item, setItem] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);

    const { isSync } = useBaseProvider();

    const handleAddSelected = async (selectedItems) => {
        if (!selectedItems.length) return;

        const existingItemIds = new Set(data.map(i => i.ItemID));

        for (const a of selectedItems) {
            if (existingItemIds.has(a.ID)) continue;

            try {
                const newId = await getNextId("SessionForRequest");

                const newItem = {
                    ID: newId,
                    ItemID: a.ID,
                    UnitID: a.UnitID,
                    Requested: 0,
                    Executed: 0,
                    Minimum: 0,
                    ShelfOnHand: 0,
                    StockOnHand: 0,
                    SessionID: id,
                    Remarks: "",
                    SyncStatus: SyncStatusMap.create
                };

                await addRecord('SessionForRequest', newItem);
            } catch (error) {
                console.error(`Failed to add item ${a.ID}:`, error);
            }
        }

        fetchData();
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoading(true);

            try {
                await fetchData();
            } catch (error) {
                console.error("Error during initial data fetch:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchInitialData();
    }, [id]);

    useEffect(() => {
        const handleRefresh = () => {
            fetchData();
        };

        window.addEventListener("refreshSession", handleRefresh);

        return () => {
            window.removeEventListener("refreshSession", handleRefresh);
        };
    }, [id]);


    const fetchData = async () => {

        try {
            const orderRequest = await getAll('SessionForRequest');
            const sess = getRecordById('session', id);
            const it = await getAll("item");
            const ut = await getAll("unit");
            const sessionOR = orderRequest.filter((a) => a.SessionID === Number(id) && a.SyncStatus !== SyncStatusMap.done);
            const mergedData = sessionOR.map((inv) => {
                const itemDetails = it.find(i => i.ID === inv.ItemID) || {};
                const unitDetails = ut.find(u => u.ID === inv.UnitID) || {};

                return {
                    ...inv,
                    Item: itemDetails,
                    Unit: unitDetails
                };
            });
            setData(mergedData);
            setSession(sess);
            setItem(it);

        } catch (error) {
            console.log(error);
        }
    };

    const handleDelete = async () => {
        const toDelete = Object.keys(checkedRows).filter(key => checkedRows[key]);

        // Delete all matching records in parallel
        await Promise.all(
            toDelete.map(itemID =>
                deleteRecordByField('SessionForRequest', 'ItemID', parseInt(itemID))
            )
        );

        fetchData();
        setCheckedRows({});
    };

    const handleInputChange = async (row, event) => {
        if (isSync) return;

        const value = event.target.value;

        if (/^\d*$/.test(value)) {
            const parsedQty = value === "" ? "" : Math.max(0, parseInt(value, 10));

            const getItem = await getRecordById('SessionForRequest', row.ID)

            const updateItem = {
                ...getItem,
                Requested: parsedQty === "" ? 0 : parsedQty,
                SyncStatus: getItem.SyncStatus === SyncStatusMap.create ? SyncStatusMap.create : SyncStatusMap.update
            }

            await updateRecord('SessionForRequest', updateItem);
            fetchData();
        }
    };

    const handleQuantityChange = async (row, change) => {
        if (isSync) return;

        const currentQty = row?.Requested || 0;

        const newQuantity = Math.max(0, currentQty + change);

        const getItem = await getRecordById('SessionForRequest', row.ID)

        const updateItem = {
            ...getItem,
            Requested: newQuantity,
            SyncStatus: getItem.SyncStatus === SyncStatusMap.create ? SyncStatusMap.create : SyncStatusMap.update
        }

        await updateRecord('SessionForRequest', updateItem);
        fetchData();
    };

    const handleCellClick = (row, type) => {
        if (isSync) return;

        setSelectedRow(row);
        if (type === "unit") {
            setShowUnitModal(true);
        } else if (type === "remarks") {
            setShowRemarksModal(true);
        }

    }

    const handleUnitSelect = async (item) => {
        try {
            const order = await getRecordById('SessionForRequest', selectedRow.ID)

            const updateOrder = {
                ...order,
                UnitID: item.ID
            }
            await updateRecord('SessionForRequest', updateOrder);
            fetchData();
        } catch (error) {
            console.log('update error');
        }
    }

    const handleRemarks = async (value) => {
        try {
            const order = await getRecordById("SessionForRequest", selectedRow.ID);

            const updateOrder = {
                ...order,
                Remarks: value,
                SyncStatus: order.SyncStatus === SyncStatusMap.create ? SyncStatusMap.create : SyncStatusMap.update
            };

            await updateRecord('SessionForRequest', updateOrder);

            fetchData();

        } catch (error) {
            console.error("Failed to update remarks");
        }
    };

    return (
        <div className="opal-order-request mb-2 d-none d-md-block">
            <Accordion defaultActiveKey="0" alwaysOpen>
                <Accordion.Item eventKey="0">
                    <Accordion.Header className="p-0 mb-2">
                        <div className="header-container">
                            <div className="header-left">
                                <TauriImage localImage={section.icon} className="tat-icon me-2" />
                                <span className="header-title">{section?.header.toUpperCase()}</span>
                            </div>
                            <div className="d-flex align-items-center">
                                {Object.values(checkedRows).some(checked => checked) && (
                                    <Button
                                        className="btn d-flex align-items-center justify-content-center p-0"
                                        size="sm"
                                        disabled={isSync}
                                        onClick={(e) => {
                                            e.stopPropagation(); // ✅ Prevent accordion toggle
                                            handleDelete(); // ✅ Open modal
                                        }}
                                    >
                                        <TauriImage localImage="/Images/delete-icon.svg" className="tat-icon me-2" color={"#EF6C00"} />
                                        <div>Delete Item</div>
                                    </Button>
                                )}
                                <Button
                                    className="btn d-flex align-items-center justify-content-center p-0"
                                    size="sm"
                                    disabled={isSync}
                                    onClick={(e) => {
                                        e.stopPropagation(); // ✅ Prevent accordion toggle
                                        setShowItemModal(true); // ✅ Open modal
                                    }}
                                >
                                    <TauriImage localImage="/Images/flat-color-icons--plus.svg" className="tat-icon me-2" />
                                    <div>Add Item</div>
                                </Button>
                                <div className="item-count">{data?.length} items</div>
                            </div>
                        </div>
                    </Accordion.Header>

                    <Accordion.Body className="py-0 px-0">
                        {loading ? (
                            <TauriLoader2 width="2.5rem" height="2.5rem" borderwidth="0.35rem" color="#1D5D9B" />
                        ) : (
                            <div className="OR-table">
                                <table className="table table-striped">
                                    <thead className="OR-table-header">
                                        <tr>
                                            <th>ITEM CODE</th>
                                            <th>ITEM DESCRIPTION</th>
                                            <th className="text-center">UNIT</th>
                                            <th className="text-center">QUANTITY</th>
                                            <th>REMARKS</th>
                                        </tr>
                                    </thead>
                                    <tbody className="OR-table-body">
                                        {data.map((row) => (
                                            <tr key={row.ID}>
                                                <td>{row?.Item?.Code}</td>
                                                <td>{row?.Item?.Description}</td>
                                                <td className="text-center clickable" onClick={() => handleCellClick(row, "unit")}>
                                                    {row.UnitID ?
                                                        row?.Unit?.Description?.toUpperCase() : <TauriImage
                                                            localImage={"/Images/flat-color-icons--plus-primary.svg"}
                                                            className={"tat-icon"}
                                                            alt="icon"
                                                        />
                                                    }
                                                </td>
                                                <td className="quantity-cell text-nowrap text-center">
                                                    <TauriImage
                                                        localImage={"/Images/uil--plus.svg"}
                                                        alt="icon"
                                                        onClick={() => handleQuantityChange(row, 1)}
                                                        color={"#1D5D9B"}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="mx-1"
                                                        value={row.Requested ?? 0}
                                                        onChange={(event) => handleInputChange(row, event)}
                                                    />
                                                    <TauriImage
                                                        localImage={"/Images/uil--minus.svg"}
                                                        alt="icon"
                                                        onClick={() => handleQuantityChange(row, -1)}
                                                        color={"#1D5D9B"}
                                                    />
                                                </td>
                                                <td className="clickable" onClick={() => handleCellClick(row, "remarks")}>
                                                    {row?.Remarks}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                        )}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
            <OpalAddItemRequestModal
                show={showItemModal}
                onHide={() => setShowItemModal(false)}
                data={item}
                onAddSelected={handleAddSelected}
            />
            <OpalUnitSelectorModal
                show={showUnitModal}
                onHide={() => setShowUnitModal(false)}
                onSelect={handleUnitSelect}
            />
            <OpalAddRemarksModal
                show={showRemarksModal}
                onHide={() => setShowRemarksModal(false)}
                display={selectedRow?.Remarks}
                onUpdate={handleRemarks}
            />
        </div>
    );
};

export default OpalOrderRequest;